import { z } from 'zod';
import { defineStepType } from '../contract.js';
import { fileCategorySchema } from '../../files/categories.js';
/**
 * A real state form, completed on a phone.
 *
 * The candidate must see the actual I-9, the Virginia sworn disclosure, the APS mandated
 * reporter form — the documents themselves, not HTML that approximates them. What gets filed
 * has to be recognisable to whoever receives it, and re-implementing a state form as a web
 * form means maintaining a copy that drifts from the original every time the state revises it.
 *
 * The template VERSION is pinned inside the config, which is inside the definition's hashed
 * content. So a state revision is a material edit by construction: it produces a new
 * definition version, and every instance issued before it keeps rendering and filling against
 * the form the person actually saw.
 */
/**
 * What a field-values map may contain.
 *
 * Exported so the API's submission preparer can apply the SAME rule before it stages
 * anything. Checking values by hand there let an empty key through — it satisfied a
 * hand-written "is it a string" test and then failed `z.string().min(1)` in this schema,
 * after the artifact had already been staged and marked accepted.
 */
export const pdfFormValuesSchema = z.record(z.string().min(1), z.union([z.string(), z.boolean()]));
/**
 * The shape the portal sends — for the draft save, the submit button, and the server.
 *
 * `values` is deliberately `unknown`-valued here and not `pdfFormValuesSchema`: a
 * half-finished draft legitimately holds values the fill will refuse, and the button rule
 * must not crash on one. What this schema is for is the OUTER shape, and specifically that
 * `values` is a record at all.
 *
 * That was reachable. `Object.entries` coerces: a body of `{ values: 'abc' }` becomes the
 * entries `0:'a'`, `1:'b'`, `2:'c'` — every key a non-empty string, every value a string, so
 * it satisfies `pdfFormValuesSchema` and fills any field whose id happens to be a small
 * number. State forms do have such ids. An array coerces the same way, and a number coerces
 * to nothing at all, which submits a blank form.
 */
export const pdfFormDraftSchema = z.object({
    values: z.record(z.string(), z.unknown()),
    complete: z.boolean().default(false),
});
/** Opt-in conditions for signed offers; ordinary questionnaires may still answer false. */
export const pdfAcceptanceConditionsSchema = z.object({
    submissionDeadline: z.string().datetime().nullable().default(null),
    acceptanceField: z.string().min(1).nullable().default(null),
    signatureField: z.string().min(1).nullable().default(null),
});
export function checkPdfAcceptance(config, values, now) {
    const parsed = pdfAcceptanceConditionsSchema.safeParse(config);
    if (!parsed.success)
        return { ok: false, message: 'This form is not configured correctly.' };
    const c = parsed.data;
    if (c.submissionDeadline !== null && Date.parse(now) > Date.parse(c.submissionDeadline)) {
        return { ok: false, message: 'This offer has expired. Contact HR for a new reviewed offer.' };
    }
    if (c.acceptanceField !== null && values[c.acceptanceField] !== true) {
        return { ok: false, message: 'Confirm that you accept the offered terms before signing.' };
    }
    if (c.signatureField !== null && (typeof values[c.signatureField] !== 'string' || String(values[c.signatureField]).trim() === '')) {
        return { ok: false, message: 'Type your full name to sign the offer.' };
    }
    return { ok: true };
}
export const pdfForm = defineStepType({
    type: 'pdf-form',
    /** The person completes the form; the server only flattens what they entered. */
    submittedBy: 'person',
    label: 'Official form',
    description: 'Presents a real PDF form for the person to complete, and files the flattened result.',
    configSchema: z.object({
        heading: z.string().min(1),
        instructions: z.string().default(''),
        templateId: z.string().min(1),
        /**
         * Pinned, and the only input to the artifact forever. Regenerating against a later
         * version would change what a person is recorded as having signed.
         */
        templateVersion: z.number().int().positive(),
        /**
         * Values the system supplies rather than asking for — the person's own name, today's
         * date. Keyed by field id, valued by what to read it from.
         */
        bindings: z.record(z.string().min(1), z.enum(['person.fullName', 'person.email', 'today'])).default({}),
        target: fileCategorySchema,
        requirementKey: z.string().min(1).nullable().default(null),
        ...pdfAcceptanceConditionsSchema.shape,
    }),
    submissionSchema: z.object({
        /**
         * Field values keyed by the template's own field ids. A checkbox is a boolean, everything
         * else is text — the same shape `fillTemplate` takes.
         */
        values: pdfFormValuesSchema,
        /**
         * The flattened PDF the SERVER produced, staged like any other upload.
         *
         * Produced server-side on submit, never in the browser: a client-rendered PDF is a
         * document the person's own machine composed, and this one is evidence.
         */
        uploadId: z.string().min(1),
        fileName: z.string().min(1),
        /**
         * What the SERVER printed, keyed by server-filled field id (ADR-014 §1). Never an answer:
         * the preparer refuses a submitted value for any of these fields by name. The sensitive
         * sources (SSN, date of birth) hold only `'on-file'`; see `checkPrintedValues`.
         *
         * Defaulted, so every submission recorded before ADR-014 still parses.
         */
        printed: z.record(z.string().min(1), z.string()).default({}),
    }),
    /**
     * The client draft carries field values and a flag; the SERVER produces the flattened PDF,
     * its upload id and its filename. So the draft can never satisfy `submissionSchema`, and
     * the default rule would leave the submit button disabled for ever.
     *
     * Completeness is decided by the PORTAL and recorded in the draft, because only the portal
     * has the pinned template and therefore knows which fields the form marks required — the
     * config carries a template id and version, not a field list. That makes this a
     * client-asserted flag, which is fine for a button: `fillTemplate` re-checks every required
     * field server-side and refuses with per-field messages, so a wrong flag costs a refusal
     * rather than a bad record.
     */
    readyToSubmit: ({ config, draft }) => {
        const parsed = pdfFormDraftSchema.safeParse(draft);
        return parsed.success && parsed.data.complete === true &&
            (config.acceptanceField === null || parsed.data.values[config.acceptanceField] === true) &&
            (config.signatureField === null || (typeof parsed.data.values[config.signatureField] === 'string' && String(parsed.data.values[config.signatureField]).trim() !== ''));
    },
    checkSubmission: async ({ config, submission, now }) => checkPdfAcceptance(config, submission.values, now),
    /**
     * pdf-form is person-signed when the template has a signature field (ADR-014 §3, #215). A
     * questionnaire with no such field is filled in, not signed, and records no evidence.
     *
     * The name comes from the field the CONFIG names, not from a conventional key: which field
     * holds the signature is a property of the template, and guessing would put the wrong value
     * on a personnel-file record.
     */
    signature: ({ config, submission }) => {
        if (config.signatureField === null)
            return null;
        const typed = submission.values[config.signatureField];
        if (typeof typed !== 'string' || typed.trim() === '')
            return null;
        return { typedName: typed.trim() };
    },
    /** The flattened PDF the server staged from the field values. Always exactly one. */
    uploads: ({ submission }) => [submission.uploadId],
    onApprove: ({ instance, config, submission }) => [
        {
            kind: 'store-file',
            instanceId: instance.id,
            uploadId: submission.uploadId,
            category: config.target,
            source: 'pdf-artifact',
            // Pinned at enqueue, where the upload is in hand; a step type has no upload to plan for.
            plan: null,
        },
        ...(config.requirementKey !== null
            ? [
                {
                    kind: 'mint-credential',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    identifier: null,
                    issuedAt: null,
                    expiresAt: null,
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
});
//# sourceMappingURL=pdf-form.js.map